import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

import { AuthenticationService } from '@common/services';
import { InsightsService } from '@core/services';
import { CoreMenuItem } from '@core/types';
import { getMenuLabel } from '@core/utils';
import { environment } from 'environments/environment';

import { CustomSwalService } from './custom-swal.service';
import { handlesOwnErrors } from './http-context';

/**
 * Ejecuta las opciones del menú de tipo acción: en vez de navegar, hacen una
 * petición HTTP a su `url` con el verbo elegido en el mantenimiento de menús
 * (GET, POST, PUT o DELETE).
 *
 * Lo comparten todos los lugares que pintan el menú (vertical, horizontal,
 * panel lateral, explorador de apps), para que una acción se comporte igual
 * desde cualquiera.
 */
@Injectable({ providedIn: 'root' })
export class MenuActionService {

  /** Acción en curso; evita dispararla dos veces con un doble clic */
  private _running: string | null = null;

  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    private _swalService: CustomSwalService,
    private _insightsService: InsightsService,
    private _authenticationService: AuthenticationService
  ) {}

  /**
   * Indica si la opción del menú es una acción y no una pantalla
   * @param item Opción del menú
   */
  isAction(item: Pick<CoreMenuItem, 'actionMethod' | 'url'>): boolean {
    return !!item?.actionMethod && !!item?.url;
  }

  /**
   * Ejecuta la acción. Las que modifican datos (POST, PUT, DELETE) piden
   * confirmación antes; un GET sale directo.
   * @param item Opción del menú de tipo acción
   */
  async run(item: CoreMenuItem): Promise<void> {
    if (!this.isAction(item) || this._running === item.id) return;

    const method = item.actionMethod.toUpperCase();
    const url = this._resolveUrl(item.url);
    const name = getMenuLabel(item, this._translateService);
    const data = { menuId: item.id, method, url };

    if (method !== 'GET') {
      const answer = await this._swalService.showQuestion(
        this._translateService.instant('MENUS.ACTION.CONFIRM_TITLE', { name }),
        this._translateService.instant('MENUS.ACTION.CONFIRM_TEXT', { method })
      );

      if (!answer.isConfirmed) {
        this._log('Canceló la ejecución de una acción del menú', data);
        return;
      }
    }

    this._running = item.id;
    this._swalService.showLoading();

    this._http.request(method, url, {
      body: method === 'POST' || method === 'PUT' ? {} : undefined,
      context: handlesOwnErrors()
    }).subscribe({
      next: () => {
        this._running = null;
        Swal.close();
        this._log('Ejecutó una acción del menú', data);
        this._swalService.showToastMsg(
          this._translateService.instant('MENUS.ACTION.SUCCESS', { name }), '', 'success'
        );
      },
      error: (error: HttpErrorResponse) => {
        this._running = null;
        Swal.close();
        this._log('Error al ejecutar una acción del menú', { ...data, status: error?.status }, error, true);
        this._swalService.showErrorMsg(this._errorMessage(error, name));
      }
    });
  }

  /**
   * Una url absoluta se usa tal cual; una relativa se toma como ruta de
   * nuestras apis (`/common/api/...`), igual que las demás peticiones.
   * @param link Url guardada en el menú
   */
  private _resolveUrl(link: string): string {
    const clean = (link || '').trim();
    if (/^https?:\/\//i.test(clean)) return clean;
    return `${environment.apiUrl}/${clean.replace(/^\/+/, '')}`;
  }

  /**
   * Mensaje del error: el que manda nuestra api (es una clave de traducción),
   * o uno genérico con el nombre de la acción.
   * @param error Respuesta de error
   * @param name Nombre de la acción
   */
  private _errorMessage(error: HttpErrorResponse, name: string): string {
    const fallback = this._translateService.instant('MENUS.ACTION.FAIL', { name });
    if (!error?.url?.startsWith(environment.apiUrl)) return fallback;

    const key = error?.error?.message;
    if (typeof key !== 'string' || !key) return fallback;

    const translated = this._translateService.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private _log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.Menus, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.Menus, message, data, user);
  }
}
